import { PROJECTS_PATH, PROJECT_ROUTE_TEMPLATE } from '../projects/project-scope';
import type { NavItem } from './navigation.models';

/**
 * App-market apps whose screens also open scoped to a project — the single
 * declaration of "supports projects".
 *
 * Each id is a primary `APP_NAVIGATION` entry. The Projects pane lists
 * one group per app here, built from that entry (same pages, same access
 * rules) with its routes under `/projects/:projectId`. An app joins with one
 * entry here plus its `projects/:projectId/<app>` route in `app.routes.ts`,
 * and its transport building its base from `ACTIVE_PROJECT_ID`.
 */
export const PROJECT_APPS: readonly string[] = ['tasker', 'scheduler', 'shifts'];

function inProject(item: NavItem): NavItem {
  return {
    ...item,
    id: `projects-${item.id}`,
    route: `${PROJECT_ROUTE_TEMPLATE}${item.route}`,
    children: item.children?.map(inProject),
  };
}

/**
 * The Projects entry for a list of primary entries. Its children are groups,
 * one per enabled project app; navigation filtering drops a group the session
 * may not reach and the entry itself once no group is left.
 *
 * Listing projects is employer-only on the backend (`/workspaces/projects`),
 * hence `settings:manage`.
 */
export function projectsNavItem(items: readonly NavItem[]): NavItem {
  return {
    id: 'projects',
    label: 'nav.projects',
    icon: '@tui.folder-kanban',
    route: PROJECTS_PATH,
    projectScoped: true,
    access: { permissions: ['settings:manage'] },
    children: PROJECT_APPS.flatMap((id) => {
      const app = items.find((item) => item.id === id);

      return app ? [inProject(app)] : [];
    }),
  };
}
